import Chart, { useChart } from 'src/components/chart';
import { useMemo, useRef } from 'react';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { Box, useTheme } from '@mui/system';

type Props = {
    data: any[];
    rateMode: 'blended' | 'winrate' | 'paid' | string;
    mainDomain?: string;
    isAbsMode: boolean;
};

export default function ChartLandscape({ data, rateMode, mainDomain, isAbsMode }: Props) {
    const safeData = Array.isArray(data) ? data : [];
    const hoveredSeriesRef = useRef<number | null>(null); // ⚡ ref pour stocker l’index
    const theme = useTheme();

    // ✅ Sélection de la bonne métrique selon mode
    const metricKey = useMemo(() => {
        if (rateMode === 'blended') return isAbsMode ? 'abs_top_blended_presence_rate' : 'top_blended_presence_rate';
        if (rateMode === 'winrate') return isAbsMode ? 'abs_top_competitor_win_rate' : 'top_competitor_win_rate';
        return isAbsMode ? 'abs_top_paid_presence_rate' : 'top_paid_presence_rate';
    }, [rateMode, isAbsMode]);

    // ✅ Liste des domaines uniques
    const domains = useMemo(
        () => Array.from(new Set(safeData.map((d: any) => d.domain_name))),
        [safeData]
    );

    // ✅ Dates : supporte date_day et date_group
    const categories = useMemo(
        () => Array.from(new Set(safeData.map((d: any) => d.date_group || d.date_day))).sort(),
        [safeData]
    );

    // ✅ Top 5 domaines par somme des scores
    const topDomains = useMemo(() => {
        const scores = new Map<string, number>();
        for (const d of safeData) {
            scores.set(d.domain_name, (scores.get(d.domain_name) ?? 0) + (d[metricKey] ?? 0));
        }
        return Array.from(scores.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name]) => name);
    }, [safeData, metricKey]);

    // ✅ Construction des séries
    const series: any[] = useMemo(
        () =>
            topDomains.map((domain) => ({
                name: domain,
                data: categories.map((date) => {
                    const point = safeData.find(
                        (d: any) => d.domain_name === domain && (d.date_group || d.date_day) === date
                    );
                    return { x: date, y: point ? point[metricKey] : null };
                }),
            })),
        [topDomains, categories, safeData, metricKey]
    );

    // 🎯 Palette : tout en gris sauf le domaine principal
     const palette = useMemo(() => {
        const OTHER = ['#DFD09D', '#11B5FF', '#7CD3AD', '#DDBFE9']
        const MAIN = '#B48FFF';

        return series.map((s, i) => {
            if (s.name === mainDomain) return MAIN;

            const offset = i > series.findIndex(x => x.name === mainDomain) ? i - 1 : i;

            return OTHER[offset % OTHER.length];
        });
    }, [series, mainDomain]);  
 
    // ✅ Titre axe Y
    const yTitle = useMemo(() => {
        if (rateMode === 'blended') return 'Blended Presence Rate';
        if (rateMode === 'winrate') return 'Competitor Win Rate';
        return 'Paid Presence Rate';
    }, [rateMode]);

    // ✅ Max Y dynamique
    const maxY = useMemo(() => {
        let max = 0;
        for (const s of series) {
            for (const pt of s.data) {
                if (pt.y !== null && pt.y !== undefined) max = Math.max(max, pt.y);
            }
        }
        return Math.ceil(max / 10) * 10;
    }, [series]);

    const workspace = useCurrentWorkspace();

    const chartOptions = useChart({
        chart: {
            type: 'line',
            toolbar: { show: false },
            zoom: { enabled: false },
            events: {
                dataPointMouseEnter (config) {
                    hoveredSeriesRef.current = config.seriesIndex; // ⚡ update ref
                },
                dataPointMouseLeave () {
                    hoveredSeriesRef.current = null; // reset ref
                },
                mouseMove: (event: any, chartContext: any, config: any) => {
                    const { seriesIndex } = config;

                    // reset styles
                    document.querySelectorAll('.apexcharts-series').forEach(el => {
                        (el as HTMLElement).style.opacity = '0.3';
                        (el as HTMLElement).style.strokeWidth = '2px';
                    });

                    // highlight only hovered serie
                    if (seriesIndex >= 0) {
                        const el = document.querySelector(`.apexcharts-series[data\\:realIndex="${seriesIndex}"]`) as HTMLElement;
                        if (el) {
                            el.style.opacity = '1';
                            el.style.strokeWidth = '4px';
                        }
                    }
                },
                mouseLeave: () => {
                    // reset everything
                    document.querySelectorAll('.apexcharts-series').forEach(el => {
                        (el as HTMLElement).style.opacity = '1';
                        (el as HTMLElement).style.strokeWidth = '2px';
                    });
                },
            }
        },
        colors: palette,
        stroke: { curve: 'straight', width: 2 },
        markers: {
            size: 0,
            colors: palette,
            strokeColors: palette,
        },

        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const date = w.globals.categoryLabels[dataPointIndex];

                // ⚡ Nom exact de la série actuellement survolée
                const activeSeriesName = w.globals.hoverSeries?.name || w.globals.seriesNames[seriesIndex];

                const items = w.config.series.map((s: any, i: number) => ({
                    name: s.name,
                    value: series[i][dataPointIndex],
                    isHovered: s.name === activeSeriesName,
                }));

                // 👉 Tri décroissant
                items.sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0));

                const rows = items.map((item: any) => `
                    <div style="display:flex; justify-content:space-between; align-items:center; margin:4px 0; width:100%;
                                ">
                        <div style="display:flex; align-items:center; gap:8px;">
                        <span style="display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; background:white; border-radius:50%;">
                            <img src="https://www.google.com/s2/favicons?domain=${item.name}&sz=32"
                                style="width:16px; height:16px; border-radius:3px;" />
                            </span>

                        <span style="color: ${theme.palette.mode === 'dark' ? "#FFF" : "black"}; font-size:13px; font-weight:${item.isHovered ? "900" : "500"};">
                            ${item.name}
                        </span>
                        </div>
                        <span style="color: ${theme.palette.mode === 'dark' ? "#FFF" : "black"}; font-size:13px; font-weight:${item.isHovered ? "900" : "500"};">
                        ${typeof item.value === "number" ? item.value.toFixed(0) + "%" : "-"}
                        </span>
                    </div>
                    `).join("");

                return `
                    <div style="border-radius:12px; border: ${theme.palette.mode === 'dark' ? "1px solid #2A323B" : "1px solid white"}; background: ${theme.palette.mode === 'dark' ? "rgba(33,43,54,0.8)" : "rgba(255,255,255,0.8)"}; padding:12px 16px; min-width:220px;">
                        <div style="color:${theme.palette.mode === 'dark' ? "#FFF" : "black"}; font-size:14px; font-weight:700; margin-bottom:6px;">${date}</div>
                        ${rows}
                    </div>
                    `;
            }
        },

        legend: { show: false },
        dataLabels: {
            enabled: true,
            textAnchor: 'middle',

            formatter: (_val: number, { seriesIndex, dataPointIndex, w }) => {
                const seriesData = w.config.series[seriesIndex].data;
                const isLast = dataPointIndex === seriesData.length - 1;
                if (isLast && w.config.series[seriesIndex].name === workspace?.url) return w.config.series[seriesIndex].name;
                return '';
            },
            background: {
                enabled: true,
                borderRadius: 3,
                padding: 12,
                opacity: 0.9,
                borderColor: "1px solid rgba(255, 255, 255, 0.20)",
            },
            style: {
                fontSize: '12px',
            },
            offsetY: -10,
        },
        xaxis: {
            categories,
            labels: { style: { colors: '#aaa', fontSize: '12px' }, rotate: -45 },
        },
        yaxis: {
            min: 0,
            max: maxY || 100,
            tickAmount: 8,
            labels: { formatter: (val: number) => `${val?.toFixed(0)}%`, style: { colors: '#aaa', fontSize: '12px' } },
            title: {
                text: yTitle,
                style: {
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#aaa'
                }
            },
        },
        grid: {
            borderColor: 'rgba(255,255,255,0.2)',
            strokeDashArray: 4,
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } },
        },
    });

    return (
        <Box sx={{ minHeight: 0, width: '100%', height: '100%', px: 2 }}>
            <Chart
                options={chartOptions}
                series={series}
                type="line"
                width="100%"
                height='100%'
            />
        </Box>
    );

}
